import { ElementNode, Text, View, Show, hexColor, IntrinsicTextNodeStyleProps } from "@lightningtv/solid";
import { Button, Row } from "@lightningtv/solid-ui";
import { useParams } from "@solidjs/router";
import { createEffect, createResource, createSignal } from "solid-js";
import { playListPassword, playListUrl, playListUsername, setIsPlaying, STREAM_EXTENSION_STORAGE_KEY } from "../../state";
import ContentBlock from "../components/ContentBlock";
import { useNavigate, useLocation } from "@solidjs/router";
import * as provider from "../api/providers/entity";

import videoPlayer from "../video";
import { fontFamily, isRTL, t } from "../translations/configuration";
import LoadingIndicator from "./loading/loading";
import { getContinueWatchingTime } from "../api/providers/moviesContinueWatching";
import { addMovieToFavorites, removeMovieFromFavorites, isMovieFavorite } from "../api/providers/moviesFavorites";
import Header from "../components/Header";
import { IconButton } from "../components/playlistComponents/TopRightHeaders";

const Entity = () => {
  const params = useParams();

  let columnRef: any, backdropRef: any, entityActions: any, settingsRef: any, privacyCheck: any;
  let myButton: any; let headerRef: any;

  const navigate = useNavigate();

  // Restore instantly from cache when returning from player (avoids loading flash).
  const _cached: any = provider.getCachedInfo(params.type, params.id);
  const _cachedBg = _cached?.backgroundImage?.();

  const [movieName, setMovieName] = createSignal<any>(
    _cached ? _cached.heroContent.title() : null
  );
  const [isVideoPlaying, setIsVideoPlaying] = createSignal(false);
  const [valueOfStream, setValueOfStream] = createSignal<any>(
    _cached ? _cached.idOfStream() : null
  );
  const [backgroundImageEntity, setBackGroundImage] = createSignal<any>(
    _cachedBg ? (Array.isArray(_cachedBg) ? _cachedBg[0] : _cachedBg) : null
  );
  const stored = JSON.parse(localStorage.getItem("movieCategoryIDs")) || [];
  const [lockedCategoryIDs, setLockedCategoryIDs] = createSignal(stored);
  const [isFavorite, setIsFavorite] = createSignal(false);

  // Start ready immediately if cache hit — no loading indicator on return navigation.
  const [isReady, setIsReady] = createSignal(!!_cached);

  const [backdropAlpha, setBackdropAlpha] = createSignal(0);
  const [containerExctencion, setContainerExctencion] = createSignal<any>(
    _cached ? _cached.containerExtencion() : null
  );
  const [savedDataShow, setSavedData] = createSignal(null);

  const [data]: any = createResource(
    () => ({ ...params }),
    provider.getInfo,
    { initialValue: _cached }
  );

  createEffect(() => {
    const currentData: any = data();

    if (videoPlayer) videoPlayer.destroy();

    if (currentData) {
      setIsReady(true);
      setContainerExctencion(currentData.containerExtencion());
      const bg = currentData.backgroundImage?.();
      if (bg) setBackGroundImage(Array.isArray(bg) ? bg[0] : bg);
      setMovieName(currentData.heroContent.title());
      //@ts-ignore
      setValueOfStream(currentData.idOfStream());
    }
  });

  createEffect(() => {
    const streamId = valueOfStream();
    if (streamId) {
      setIsFavorite(isMovieFavorite(streamId));
    }
  });

  const columnY = 640;

  const Backdrop = {
    color: hexColor("#000000"),
    alpha: 0,
    width: 1900,
    height: 890,
    x: -160,
    y: columnY,
    borderRadius: 30,
  };

  function onRowFocus(this: ElementNode) {
    //@ts-ignore
    this.children.selected?.setFocus();
    columnRef.y = columnY;
    backdropRef.y = columnY;
    backdropRef.alpha = 0;
  }

  const onEscape = () => {
    if (isVideoPlaying() === true) {
      videoPlayer.destroy()
        .then(() => console.log("Video player destroyed."))
        .catch(error => console.error("Error removing video player:", error));
      setIsVideoPlaying(false);
    } else {
      setIsVideoPlaying(false);
      document.getElementsByTagName("canvas")[0].focus();
      setTimeout(() => navigate(-1), 100);
      setIsPlaying(true);
    }
  };

  const safeCall = (fn: any, fallback = "") => typeof fn === "function" ? fn() : fallback;

  const getPosterUrl = (currentData: any): string => {
    const backdrop = safeCall(currentData?.movieImage);
    return (Array.isArray(backdrop) ? backdrop[0] : backdrop) || "";
  };

  function onEnterTrailer() {
    if (valueOfStream()) {
      const currentData: any = data();
      const posterImage = getPosterUrl(currentData);
      const movieRating = safeCall(currentData?.heroContent?.rating) || 0;
      navigate(
        `/player?str=${valueOfStream()}&type=movie&name=${encodeURIComponent(movieName()!)}&extc=${containerExctencion()}&poster=${encodeURIComponent(posterImage)}&rating=${movieRating}`
      );
    }
  }

  function onVodResume() {
    if (valueOfStream()) {
      const streamOfID = valueOfStream();
      const nameOfMovie = movieName();
      const extc = containerExctencion();
      const ext = localStorage.getItem(STREAM_EXTENSION_STORAGE_KEY) ?? extc;
      const currentVideoUrl = `${playListUrl()}/movie/${playListUsername()}/${playListPassword()}/${streamOfID}${ext}`;
      const continueTime = getContinueWatchingTime(currentVideoUrl);
      if (continueTime) {
        const currentData: any = data();
        const posterImage = getPosterUrl(currentData);
        const movieRating = safeCall(currentData?.heroContent?.rating) || 0;
        navigate(`/player?str=${streamOfID}&type=movie&name=${encodeURIComponent(nameOfMovie!!)}&extc=${ext}&seek=${continueTime}&poster=${encodeURIComponent(posterImage)}&rating=${movieRating}`);
      }
    }
  }

  createEffect(() => {
    const streamOfID = valueOfStream();
    const extc = containerExctencion();
    if (!streamOfID || !extc) { setSavedData(false); return; }
    const currentVideoUrl = `${playListUrl()}/movie/${playListUsername()}/${playListPassword()}/${streamOfID}.${extc}`;
    const continueTime = getContinueWatchingTime(currentVideoUrl);
    setSavedData(continueTime && continueTime > 0 ? true : false);
  });

  function onLock() {
    const streamId = valueOfStream();
    const currentData: any = data();
    if (!streamId || !currentData) return;
    const movieData = {
      stream_id: streamId,
      name: movieName() || "",
      category_id: currentCategoryID || "",
      poster: safeCall(currentData?.movieImage),
      rating: safeCall(currentData?.heroContent?.rating) || 0,
    };
    if (isFavorite()) {
      if (removeMovieFromFavorites(streamId)) setIsFavorite(false);
    } else {
      if (addMovieToFavorites(movieData)) setIsFavorite(true);
    }
  }

  const location = useLocation();
  const segments = location.pathname.split("/").filter(Boolean);
  const currentCategoryID = segments.length > 0 ? Number(segments[segments.length - 1]) : null;

  return (
    <Show when={data()}>
      <View src={`${backgroundImageEntity()}`}>
        <View
          onEscape={() => {
            if (!location.pathname.startsWith("/entity")) return;
            onEscape();
          }}
          src={"https://i.ibb.co/NrtvBBD/hero-mask-inverted.png"}
        >
          <View color={backgroundImageEntity() ? hexColor("#00000000") : hexColor("#0d0f14")}>

            <Header screen={""} ref={(el) => headerRef = el}
              onRight={() => isRTL() ? null : settingsRef?.setFocus()}
              onLeft={() => isRTL() ? settingsRef?.setFocus() : null}
              onDown={() => entityActions?.setFocus()} />

            {/* ── Top-right: Favorite + Settings ── */}
            {/* <View x={isRTL() ? 2 : 1490} y={46}>
              <View
                onRight={() => settingsRef.setFocus()}
                onDown={() => entityActions.setFocus()}
                ref={privacyCheck}
                x={120}
                width={90} height={90}
                borderRadius={45}
                style={{
                  color: hexColor("#1a1d2499"),
                  focus: { color: hexColor("#1e3a5f") },
                  transition: { color: { duration: 150 } },
                  border: { width: 1, color: hexColor("#2d3748") },
                }}
                onEnter={onLock}
              >
                <View
                  x={24} y={24}
                  width={42} height={42}
                  src={
                    isFavorite()
                      ? "https://i.ibb.co/pRyqmgX/icons8-love-100-2.png"
                      : "https://i.ibb.co/kHcHCDb/icons8-love-100.png"
                  }
                />
              </View>
            </View> */}

            <View x={isRTL() ? 50 : 1700} y={50}>
              <IconButton
                onRef={(el) => { settingsRef = el; }}
                x={100} y={0}
                src="https://i.ibb.co/Jjp8bmn4/setting.png"
                onEnter={() => { navigate("/userInfo") }}
                onLeft={() => isRTL() ? null : headerRef?.setFocus()}
                onRight={() => isRTL() ? headerRef?.setFocus() : null}
                onDown={() => entityActions?.setFocus()}
              />
            </View>

            <View onLeft={() => true} x={170}>
              <Show
                when={isReady()}
                fallback={
                  <View x={650} y={400}>
                    <LoadingIndicator isCategoriesIndicator={false} />
                  </View>
                }
              >
                <ContentBlock
                  y={200}
                  x={params.type == "series" ? -100 : isRTL() ? 700 : 0}
                  content={data().heroContent}
                />
              </Show>

              {/* ── Action buttons row ── */}
              <Row
                ref={entityActions}
                x={isRTL() ? 700 : 0}
                y={820}
                scroll="none"
                height={90}
                width={700}
                gap={20}
                onUp={() => settingsRef?.setFocus()}
              >
                {params.type === "movie" && (
                  <>
                    {/* ── Play Movie button ── */}
                    <View
                      width={320} height={80}
                      borderRadius={14}
                      style={{
                        color: hexColor("#1a1d24"),
                        border: { width: 0, color: hexColor("#00000000") },
                        focus: {
                          color: hexColor("#1a1d24"),
                          border: { width: 2, color: hexColor("#2563eb") },
                        },
                        transition: { color: { duration: 150 } },
                      }}
                      autofocus
                      onEnter={() => onEnterTrailer("movies")}
                    >
                      <View x={18} y={16} width={50} height={50} src="https://i.ibb.co/WNPhwwTN/Arrow-Forward.png" />
                      <Text
                        x={72} y={27}
                        style={{
                          fontFamily: fontFamily(),
                          fontSize: 30,
                          color: hexColor("#ffffff"),
                          fontWeight: isRTL() ? "normal" : "bold",
                        }}
                      >
                        {t("playMovie") || "Play Movie"}
                      </Text>
                    </View>

                    {/* ── Resume button ── */}
                    <Show when={savedDataShow()}>
                      <View
                        width={240} height={80}
                        borderRadius={14}
                        style={{
                          color: hexColor("#1a1d24"),
                          border: { width: 0, color: hexColor("#00000000") },
                          focus: {
                            color: hexColor("#1a1d24"),
                            border: { width: 2, color: hexColor("#2563eb") },
                          },
                          transition: { color: { duration: 150 } },
                        }}
                        onEnter={() => onVodResume()}
                      >
                        <Text
                          x={58} y={27}
                          style={{
                            fontFamily: fontFamily(),
                            fontSize: 30,
                            color: hexColor("#ffffff"),
                            fontWeight: isRTL() ? "normal" : "bold",
                          }}
                        >
                          {t("Resume") || "Resume"}
                        </Text>
                      </View>
                    </Show>

                    {/* ── Heart / Favorite button ── */}
                    <View
                      width={80} height={80}
                      borderRadius={40}
                      style={{
                        color: hexColor("#1a1d24"),
                        border: { width: 0, color: hexColor("#00000000") },
                        focus: {
                          color: hexColor("#1a1d24"),
                          border: { width: 2, color: hexColor("#2563eb") },
                        },
                        transition: { color: { duration: 150 } },
                      }}
                      onEnter={onLock}
                    >
                      <View
                        x={20} y={20}
                        width={40} height={40}
                        src={
                          isFavorite()
                            ? "https://i.ibb.co/pRyqmgX/icons8-love-100-2.png"
                            : "https://i.ibb.co/kHcHCDb/icons8-love-100.png"
                        }
                      />
                    </View>
                  </>
                )}
              </Row>

              <View ref={backdropRef} style={Backdrop} transition={{ alpha: true, y: true }} />
            </View>

            <View
              alpha={backdropAlpha()}
              color={hexColor("#000000")}
              zIndex={200}
              transition={{ alpha: true }}
            />
          </View>
        </View>
      </View>
    </Show>
  );
};

export default Entity;